import { SafePlugin } from '@atlaskit/editor-common/safe-plugin';
import { createSelectionClickHandler } from '@atlaskit/editor-common/selection';
import { BodiedSyncBlockSharedCssClassName, SyncBlockStateCssClassName } from '@atlaskit/editor-common/sync-block';
import { PluginKey } from '@atlaskit/editor-prosemirror/state';
import { DecorationSet, Decoration } from '@atlaskit/editor-prosemirror/view';
import { convertPMNodesToSyncBlockNodes } from '@atlaskit/editor-synced-block-provider';
import { lazyBodiedSyncBlockView } from '../nodeviews/bodiedLazySyncedBlock';
import { lazySyncBlockView } from '../nodeviews/lazySyncedBlock';
import { FLAG_ID } from '../types';
import { shouldIgnoreDomEvent } from './utils/ignore-dom-event';
import { calculateDecorations } from './utils/selection-decorations';
import { hasEditInSyncBlock, trackSyncBlocks } from './utils/track-sync-blocks';
export var syncedBlockPluginKey = new PluginKey('syncedBlockPlugin');
export var createPlugin = function createPlugin(options, pmPluginFactoryParams, syncBlockStore, api) {
  var _ref = options || {},
    _ref$useLongPressSele = _ref.useLongPressSelection,
    useLongPressSelection = _ref$useLongPressSele === void 0 ? false : _ref$useLongPressSele;
  return new SafePlugin({
    key: syncedBlockPluginKey,
    state: {
      // @ts-ignore - Workaround for help-center local consumption
      init: function init(_, instance) {
        var syncBlockNodes = instance.doc.children.filter(function (node) {
          return node.type.name === 'syncBlock';
        });
        syncBlockStore.referenceManager.fetchSyncBlocksData(convertPMNodesToSyncBlockNodes(syncBlockNodes));
        return {
          selectionDecorationSet: calculateDecorations(instance.doc, instance.selection, instance.schema),
          showFlag: false,
          syncBlockStore: syncBlockStore
        };
      },
      // @ts-ignore - Workaround for help-center local consumption
      apply: function apply(tr, currentPluginState, oldEditorState) {
        var _meta$showFlag;
        var meta = tr.getMeta(syncedBlockPluginKey);
        var showFlag = currentPluginState.showFlag,
          selectionDecorationSet = currentPluginState.selectionDecorationSet;
        var newDecorationSet = selectionDecorationSet.map(tr.mapping, tr.doc);
        if (!tr.selection.eq(oldEditorState.selection)) {
          newDecorationSet = calculateDecorations(tr.doc, tr.selection, tr.doc.type.schema);
        }
        return {
          showFlag: (_meta$showFlag = meta === null || meta === void 0 ? void 0 : meta.showFlag) !== null && _meta$showFlag !== void 0 ? _meta$showFlag : showFlag,
          selectionDecorationSet: newDecorationSet,
          syncBlockStore: syncBlockStore
        };
      }
    },
    props: {
      nodeViews: {
        syncBlock: lazySyncBlockView({
          options: options,
          pmPluginFactoryParams: pmPluginFactoryParams,
          api: api
        }),
        bodiedSyncBlock: lazyBodiedSyncBlockView({
          pluginOptions: options,
          pmPluginFactoryParams: pmPluginFactoryParams,
          api: api
        })
      },
      // @ts-ignore - Workaround for help-center local consumption
      decorations: function decorations(state) {
        var _syncedBlockPluginKey, _syncedBlockPluginKey2, _api$connectivity, _api$editorViewMode;
        var selectionDecorationSet = (_syncedBlockPluginKey = (_syncedBlockPluginKey2 = syncedBlockPluginKey.getState(state)) === null || _syncedBlockPluginKey2 === void 0 ? void 0 : _syncedBlockPluginKey2.selectionDecorationSet) !== null && _syncedBlockPluginKey !== void 0 ? _syncedBlockPluginKey : DecorationSet.empty;
        var doc = state.doc;
        var isOffline = (api === null || api === void 0 || (_api$connectivity = api.connectivity) === null || _api$connectivity === void 0 || (_api$connectivity = _api$connectivity.sharedState.currentState()) === null || _api$connectivity === void 0 ? void 0 : _api$connectivity.mode) === 'offline';
        var isViewMode = (api === null || api === void 0 || (_api$editorViewMode = api.editorViewMode) === null || _api$editorViewMode === void 0 || (_api$editorViewMode = _api$editorViewMode.sharedState.currentState()) === null || _api$editorViewMode === void 0 ? void 0 : _api$editorViewMode.mode) === 'view';
        var offlineDecorations = [];
        var viewModeDecorations = [];

        // @ts-ignore - Workaround for help-center local consumption
        state.doc.descendants(function (node, pos) {
          if (node.type.name === 'bodiedSyncBlock' && isOffline) {
            offlineDecorations.push(Decoration.node(pos, pos + node.nodeSize, {
              class: SyncBlockStateCssClassName.disabledClassName
            }));
          }
          if ((node.type.name === 'bodiedSyncBlock' || node.type.name === 'syncBlock') && isViewMode) {
            viewModeDecorations.push(Decoration.node(pos, pos + node.nodeSize, {
              class: SyncBlockStateCssClassName.viewModeClassName
            }));
          }
        });
        return selectionDecorationSet.add(doc, offlineDecorations).add(doc, viewModeDecorations);
      },
      handleClickOn: createSelectionClickHandler(['bodiedSyncBlock'], function (target) {
        return !!target.closest(".".concat(BodiedSyncBlockSharedCssClassName.prefix));
      }, {
        useLongPressSelection: useLongPressSelection
      }),
      handleDOMEvents: {
        // @ts-ignore - Workaround for help-center local consumption
        mouseover: function mouseover(view, event) {
          return shouldIgnoreDomEvent(view, event, api);
        },
        // @ts-ignore - Workaround for help-center local consumption
        mousedown: function mousedown(view, event) {
          return shouldIgnoreDomEvent(view, event, api);
        }
      }
    },
    view: function view(editorView) {
      syncBlockStore.sourceManager.setEditorView(editorView);
      return {
        destroy: function destroy() {
          syncBlockStore.sourceManager.setEditorView(undefined);
        }
      };
    },
    // @ts-ignore - Workaround for help-center local consumption
    filterTransaction: function filterTransaction(tr, state) {
      var _api$connectivity2;
      var isOffline = (api === null || api === void 0 || (_api$connectivity2 = api.connectivity) === null || _api$connectivity2 === void 0 || (_api$connectivity2 = _api$connectivity2.sharedState.currentState()) === null || _api$connectivity2 === void 0 ? void 0 : _api$connectivity2.mode) === 'offline';
      var isConfirmedSyncBlockDeletion = Boolean(tr.getMeta('isConfirmedSyncBlockDeletion'));
      // Ignore transactions that don't change the document
      // or are from remote (collab) or already confirmed sync block deletion
      // We only care about local changes that change the document
      // and are not yet confirmed for sync block deletion
      if (!tr.docChanged || !(syncBlockStore !== null && syncBlockStore !== void 0 && syncBlockStore.sourceManager.requireConfirmationBeforeDelete()) && !syncBlockStore.sourceManager.hasPendingCreation() || Boolean(tr.getMeta('isRemote')) || Boolean(tr.getMeta('isCommitSyncBlockCreation')) || !isOffline && isConfirmedSyncBlockDeletion) {
        return true;
      }
      var _trackSyncBlocks = trackSyncBlocks(syncBlockStore.sourceManager.isSourceBlock, tr, state),
        bodiedSyncBlockRemoved = _trackSyncBlocks.removed,
        bodiedSyncBlockAdded = _trackSyncBlocks.added;
      if (!isOffline) {
        if (bodiedSyncBlockRemoved.length > 0) {
          // If there are source sync blocks being removed, and we need to confirm with user before deleting,
          // we block the transaction here, and wait for user confirmation to proceed with deletion.
          // See editor-common/src/sync-block/sync-block-store-manager.ts for how we handle user confirmation and
          // proceed with deletion.
          syncBlockStore.sourceManager.deleteSyncBlocksWithConfirmation(tr, bodiedSyncBlockRemoved);
          return false;
        }
        if (bodiedSyncBlockAdded.length > 0) {
          // If there is bodiedSyncBlock node addition and it's waiting for the result of saving the node to backend (syncBlockStore.hasPendingCreation()),
          // we need to intercept the transaction and save it in insert callback so that we only insert it to the document when backend call if backend call is successful
          // The callback will be evoked by in SourceSyncBlockStoreManager.commitPendingCreation
          syncBlockStore.sourceManager.registerCreationCallback(function () {
            var _api$core;
            api === null || api === void 0 || (_api$core = api.core) === null || _api$core === void 0 || _api$core.actions.execute(function () {
              return tr.setMeta('isCommitSyncBlockCreation', true);
            });
            api === null || api === void 0 || api.core.actions.focus();
          });
          return false;
        }
      } else {
        // Disable (bodied)syncBlock node deletion/creation/edition in offline mode and trigger an error flag instead
        var _trackSyncBlocks2 = trackSyncBlocks(function (node) {
            return node.type.name === 'syncBlock';
          }, tr, state),
          syncBlockRemoved = _trackSyncBlocks2.removed,
          syncBlockAdded = _trackSyncBlocks2.added;
        var errorFlag = false;
        if (isConfirmedSyncBlockDeletion || bodiedSyncBlockRemoved.length > 0 || syncBlockRemoved.length > 0) {
          errorFlag = FLAG_ID.CANNOT_DELETE_WHEN_OFFLINE;
        } else if (bodiedSyncBlockAdded.length > 0 || syncBlockAdded.length > 0) {
          errorFlag = FLAG_ID.CANNOT_CREATE_WHEN_OFFLINE;
        } else if (hasEditInSyncBlock(tr, state)) {
          errorFlag = FLAG_ID.CANNOT_EDIT_WHEN_OFFLINE;
        }
        if (errorFlag) {
          // Use setTimeout to dispatch transaction in next tick and avoid re-entrant dispatch
          setTimeout(function () {
            api === null || api === void 0 || api.core.actions.execute(function (_ref2) {
              var tr = _ref2.tr;
              return tr.setMeta(syncedBlockPluginKey, {
                showFlag: errorFlag
              });
            });
          }, 0);
          return false;
        }
      }
      return true;
    },
    // @ts-ignore - Workaround for help-center local consumption
    appendTransaction: function appendTransaction(trs, _oldState, newState) {
      trs
      // @ts-ignore - Workaround for help-center local consumption
      .filter(function (tr) {
        return tr.docChanged;
      })
      // @ts-ignore - Workaround for help-center local consumption
      .forEach(function (tr) {
        syncBlockStore === null || syncBlockStore === void 0 || syncBlockStore.sourceManager.rebaseTransaction(tr, newState);
      });
      return null;
    }
  });
};